/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { ApiClient } from '@shared/api-client';
import { Appointment } from '@shared/models/appointment';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { getCurrentLocation } from '@tours/store/tours.selectors';
import { Store } from '@ngrx/store';
import { ToursState } from '@tours/store/tours.state';

@Injectable({
  providedIn: 'root'
})
export class AppointmentService extends ApiClient {
  constructor(
    private httpClient: HttpClient,
    private datePipe: DatePipe,
    private toursState: Store<ToursState>,
  ) {
    super();
  }

  getAppointmentsForDay(day: Date): Observable<Appointment[]> {
    const startDate = new Date(day);
    const endDate = new Date(day);
    endDate.setDate(endDate.getDate() + 1);

    const params: any = {
      'date[after]': this.datePipe.transform(startDate, 'yyyy-MM-dd'),
      'date[strictly_before]': this.datePipe.transform(endDate, 'yyyy-MM-dd')
    };

    return this.toursState.select(getCurrentLocation).pipe(
      filter(location => location !== null),
      take(1),
      switchMap(location => {
        params.location = this.extractId(location['@id']);
        return this.httpClient.get<Appointment[]>(environment.backendServer + '/api/appointments', {
          headers: this.LD_JSON_HEADERS,
          params
        });
      }),
      map(response => response['hydra:member'])
    );
  }

  getAppointment(id: string): Observable<Appointment> {
    return this.httpClient.get<Appointment>(`${environment.backendServer}/api/appointments/${this.extractId(id)}`, {
      headers: this.LD_JSON_HEADERS
    });
  }

  createAppointment(appointment: Appointment): Observable<Appointment> {
    return this.toursState.select(getCurrentLocation).pipe(
      filter(location => location !== null),
      take(1),
      switchMap(location => {
        appointment.location = location['@id'];
        return this.httpClient.post<Appointment>(`${environment.backendServer}/api/appointments`, appointment, {
          headers: this.LD_JSON_HEADERS,
          observe: 'response'
        });
      }),
      map(response => response.body)
    );
  }

  updateAppointment(appointment: Appointment): Observable<Appointment> {
    return this.httpClient.put<Appointment>(
      `${environment.backendServer}/api/appointments/${this.extractId(appointment['@id'])}`,
      appointment,
      {
        headers: this.LD_JSON_HEADERS,
        observe: 'response'
      }
    ).pipe(
      map(response => response.body)
    );
  }

  deleteAppointment(id: string): Observable<any> {
    return this.httpClient.delete(`${environment.backendServer}/api/appointments/${this.extractId(id)}`, { observe: 'response' });
  }
}
